import { useEffect, useRef, useState } from "react";

/**
 * A small themed dropdown that replaces a native <select> where its
 * OS-drawn option popup (and its un-stylable blue hover highlight) clashes with
 * the dark UI. Trigger button + a popup listbox of option buttons, matching the
 * topbar nav-menu pattern (outside-click / Escape to close). Fully CSS-themed,
 * so hover/active states use the site's amber accent.
 */
export function ReelSelect<T extends string>({
  value,
  options,
  onChange,
  label
}: {
  value: T;
  options: ReadonlyArray<{ key: T; label: string }>;
  onChange: (key: T) => void;
  /** Accessible label (e.g. "Sort"); announced together with the current value. */
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Dismiss on an outside click or Escape — only wired while open.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent): void {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const current = options.find((option) => option.key === value) ?? options[0];

  return (
    <div className="reel-select" ref={wrapRef}>
      <button
        type="button"
        className={open ? "reel-select-trigger is-open" : "reel-select-trigger"}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${current?.label ?? ""}`}
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        <span className="reel-select-value">{current?.label}</span>
        <svg
          className="reel-select-chevron"
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          aria-hidden="true"
        >
          <path d="M1 1.5 6 6.5 11 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="reel-select-menu" role="listbox" aria-label={label}>
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              role="option"
              aria-selected={option.key === value}
              className={option.key === value ? "reel-select-option is-active" : "reel-select-option"}
              onClick={() => {
                onChange(option.key);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
